import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, Timestamp, UserId } from "../../common";
import { noSuchPreset, teamRefusals } from "./refusals";
import { PresetSlugParams } from "./schemas";
import { IssueLimit } from "./team";

const Shares = z.number().int().positive().max(1_000_000_000_000);

export const ShareEntry = z
  .strictObject({
    id: z.uuid(),
    entryType: z.enum(["issued", "transferred", "reclaimed", "destroyed"]),
    actorId: UserId,
    fromUserId: UserId.nullable(),
    toUserId: UserId.nullable(),
    shares: Shares,
    reason: z.string().nullable(),
    occurredAt: Timestamp,
  })
  .meta({ id: "ShareEntry", description: "One movement of a preset's shares." });
export type ShareEntry = z.infer<typeof ShareEntry>;

export const PresetShares = z
  .strictObject({
    supply: z.number().int().nonnegative().meta({ description: "Every share held, by anyone." }),
    holdings: z.array(z.strictObject({ userId: UserId, shares: Shares })).meta({
      description: "Largest first.",
    }),
    ledger: z.array(ShareEntry).meta({ description: "The latest 100 movements, newest first." }),
    you: z.strictObject({
      canTransfer: z.boolean(),
      canIssue: z.boolean(),
      canDestroy: z.boolean(),
      issueLimit: IssueLimit.nullable().meta({
        description: "Your most generous issuing limit, or null when you have none.",
      }),
      issuedInWindow: z.number().int().nonnegative().meta({
        description: "How many you've issued in your limit's window.",
      }),
    }),
  })
  .meta({ id: "PresetShares", description: "Who holds a preset's shares, and how they moved." });
export type PresetShares = z.infer<typeof PresetShares>;

/**
 * One movement, each taking its permission: `issueShares` issues new shares to anyone on the team,
 * `transferShares` gives some of your own to someone else on it, and `destroyShares` destroys the
 * shares of people ranked below you. The owner holds every permission, and destroys anyone's.
 */
export const MoveSharesRequest = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("issue"), to: UserId, shares: Shares }),
    z.strictObject({ type: z.literal("transfer"), to: UserId, shares: Shares }),
    z.strictObject({
      type: z.literal("destroy"),
      from: UserId,
      shares: Shares,
      reason: z.string().trim().min(1).max(200).optional(),
    }),
  ])
  .meta({ id: "MoveSharesRequest" });
export type MoveSharesRequest = z.infer<typeof MoveSharesRequest>;

export const getShares = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}/shares",
      operationId: "presets.shares.get",
      summary: "Read who holds a preset's shares.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams }))
  .output(PresetShares);

export const moveShares = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/{slug}/shares/move",
      operationId: "presets.shares.move",
      summary: "Issue, transfer, or destroy shares.",
      description:
        "Writes one ledger entry and the holdings it changes in one transaction. Shares only ever go to the owner and members who have joined.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...noSuchPreset,
    CONFLICT: { message: "There aren't that many to move." },
  })
  .input(z.object({ params: PresetSlugParams, body: MoveSharesRequest }))
  .output(PresetShares);
